type ElementSelectorFn = (node: Node | EventTarget | null) => string;
type ComponentNameFn = (node: Node | EventTarget | null) => string;

export const MAX_URL_LENGTH = 100;

const truncateUrl = (url: string) =>
  url.length > MAX_URL_LENGTH ? url.slice(0, MAX_URL_LENGTH) : url;

export interface SoftNavLcpCandidate {
  size: number;
  // rAF DOMHighResTimeStamp — page-load relative, matches browser LCP startTime
  startTime: number;
  // ms from ROUTE_STARTED to element paint — the per-route duration
  renderTime: number;
  element: string;
  componentName: string;
  id: string;
  url: string;
}

export default class SoftNavLcpObserver {
  private navStartTime = 0;
  private candidate: SoftNavLcpCandidate | null = null;
  private mutationObserver: MutationObserver | null = null;
  private pendingRafs: Set<number> = new Set();
  private imageLoadCleanups: Array<() => void> = [];
  private getElementSelector: ElementSelectorFn;
  private getNearestComponentName: ComponentNameFn;

  constructor(
    getElementSelector: ElementSelectorFn,
    getNearestComponentName: ComponentNameFn
  ) {
    this.getElementSelector = getElementSelector;
    this.getNearestComponentName = getNearestComponentName;
  }

  start(navStartTime: number): void {
    this.reset();
    this.navStartTime = navStartTime;

    if (!document.body) {
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.scheduleElementCheck(node as Element);
          }
        }
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  stop(): SoftNavLcpCandidate | null {
    const result = this.candidate;
    this.reset();
    return result;
  }

  private reset(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.pendingRafs.forEach((id) => cancelAnimationFrame(id));
    this.pendingRafs.clear();
    this.imageLoadCleanups.forEach((cleanup) => cleanup());
    this.imageLoadCleanups = [];
    this.candidate = null;
    this.navStartTime = 0;
  }

  private scheduleElementCheck(el: Element): void {
    // Check the element itself plus any img/video descendants (most likely LCP candidates)
    const targets: Element[] = [el];
    el.querySelectorAll('img, video').forEach((child) => targets.push(child));

    targets.forEach((target) => {
      if (target.tagName?.toLowerCase() === 'img') {
        const img = target as HTMLImageElement;
        if (img.complete && img.naturalWidth > 0) {
          this.scheduleCheck(img);
        } else {
          const onLoad = () => this.scheduleCheck(img);
          img.addEventListener('load', onLoad, { once: true });
          this.imageLoadCleanups.push(() =>
            img.removeEventListener('load', onLoad)
          );
        }
      } else if (target.tagName?.toLowerCase() === 'video') {
        const video = target as HTMLVideoElement;
        // Use HAVE_CURRENT_DATA / loadeddata rather than HAVE_METADATA / loadedmetadata:
        // loadedmetadata fires when dimensions are known but before any frame is decoded,
        // so getBoundingClientRect may still return zero. loadeddata fires when the first
        // frame is available, which aligns with when the browser records a video LCP entry.
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          this.scheduleCheck(video);
        } else {
          const onLoadedData = () => this.scheduleCheck(video);
          video.addEventListener('loadeddata', onLoadedData, { once: true });
          this.imageLoadCleanups.push(() =>
            video.removeEventListener('loadeddata', onLoadedData)
          );
        }
      } else {
        this.scheduleCheck(target);
      }
    });
  }

  private scheduleCheck(el: Element): void {
    const rafId = requestAnimationFrame((frameTime) => {
      this.pendingRafs.delete(rafId);
      this.checkCandidate(el, frameTime);
    });
    this.pendingRafs.add(rafId);
  }

  private checkCandidate(el: Element, frameTime: DOMHighResTimeStamp): void {
    try {
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const visibleW = Math.min(rect.right, vw) - Math.max(rect.left, 0);
      const visibleH = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      if (visibleW <= 0 || visibleH <= 0) {
        return;
      }
      const size = visibleW * visibleH;
      if (size > (this.candidate?.size ?? 0)) {
        const tag = el.tagName?.toLowerCase();
        this.candidate = {
          size,
          startTime: frameTime,
          renderTime: frameTime - this.navStartTime,
          element: this.getElementSelector(el),
          componentName: this.getNearestComponentName(el),
          id: el.id ?? '',
          url:
            tag === 'img'
              ? truncateUrl((el as HTMLImageElement).currentSrc)
              : tag === 'video'
              ? truncateUrl((el as HTMLVideoElement).currentSrc)
              : '',
        };
      }
    } catch {
      // getBoundingClientRect can throw in detached elements; skip silently
    }
  }
}
